import {useState} from 'react';

export interface BasketItem {
  id: string;
  label: string;
}

export interface BasketInstance<TItem extends BasketItem = BasketItem> {
  selectedItems: TItem[];
  isItemSelected: (id: string) => boolean;
  toggleItem: (item: TItem) => void;
  addItems: (ids: TItem[] | TItem) => void;
  removeItems: (ids: string[] | string) => void;
  clearAll: () => void;
}

export default function useBasket<TItem extends BasketItem = BasketItem>(): [BasketInstance<TItem>] {
  const [selectedItems, setSelectedItems] = useState<TItem[]>([]);

  const isItemSelected = (id: string): boolean => {
    return selectedItems.some(item => item.id === id);
  };

  const addItems = (ids: TItem[] | TItem): void => {
    const itemsToAdd = Array.isArray(ids) ? ids : [ids];
    setSelectedItems(prevItems => {
      const newItems = itemsToAdd.filter(item =>
        !prevItems.some(prevItem => prevItem.id === item.id)
      );
      return [...prevItems, ...newItems];
    });
  };

  const toggleItem = (item: TItem): void => {
    if (isItemSelected(item.id)) {
      removeItems(item.id);
      return;
    }

    setSelectedItems(prevItems => [...prevItems, item]);
  };

  const removeItems = (ids: string[] | string): void => {
    const itemsToRemove = Array.isArray(ids) ? ids : [ids];
    setSelectedItems(prevItems =>
      prevItems.filter(prevItem =>
        !itemsToRemove.includes(prevItem.id)
      )
    );
  };

  const clearAll = (): void => {
    setSelectedItems([]);
  };

  return [{
    selectedItems,
    isItemSelected,
    addItems,
    removeItems,
    clearAll,
    toggleItem
  }];
};
